import { DateTimeUtils } from '@locus-taxy/encore-utils';

export type TimezoneOption = {
  value: string;
  label: string;
};

export function getTimezoneOptionLabel(
  option: TimezoneOption | string
): string {
  return typeof option === 'string' ? option : option.label;
}

export function normalizeTimezoneChangeValue(
  newValue: string | TimezoneOption | (string | TimezoneOption)[] | null
): string | null {
  const value = Array.isArray(newValue) ? newValue[0] : newValue;
  if (!value) {
    return null;
  }

  return typeof value === 'string' ? value : value.value;
}

export function getNextTimezone(
  currentTimezone: string,
  newValue: string | TimezoneOption | (string | TimezoneOption)[] | null
): string | null {
  const timezoneValue = normalizeTimezoneChangeValue(newValue);
  if (!timezoneValue || timezoneValue === currentTimezone) {
    return null;
  }
  return timezoneValue;
}

export function normalizeLocaleChangeValue(
  newValue: string | string[] | null
): string | null {
  const value = Array.isArray(newValue) ? newValue[0] : newValue;
  return value ?? null;
}

/**
 * Whether the dropdowns can actually be rendered.
 *
 * Empty option lists used to collapse each dropdown to a single entry — the current value — which
 * looked like a working control offering exactly one choice. That silent degradation was the most
 * visible symptom of DP-4312, so the page now states the failure instead of dressing it up.
 */
export function hasSelectableOptions(
  timezoneOptions: string[],
  localeOptions: string[]
): boolean {
  return timezoneOptions.length > 0 && localeOptions.length > 0;
}

/**
 * The timezone choices with their offsets. A stored timezone missing from the options is added so
 * the select can still show it; nothing is added when none is stored.
 */
export function buildTimezoneOptionsWithOffset(
  timezoneOptions: string[],
  timezone: string | null
): TimezoneOption[] {
  const dateTimeUtils = DateTimeUtils.getInstance();
  const options =
    timezone === null || timezoneOptions.includes(timezone)
      ? timezoneOptions
      : [...timezoneOptions, timezone];

  return options.map(tz => {
    const offset = dateTimeUtils.getTimezoneOffset(tz);
    return {
      value: tz,
      label: `${tz} (${offset})`,
    };
  });
}

export function isTimezoneOptionEqualToValue(
  opt: TimezoneOption | string,
  val: TimezoneOption | string
): boolean {
  const optValue = typeof opt === 'string' ? opt : opt.value;
  const valValue = typeof val === 'string' ? val : val.value;
  return optValue === valValue;
}
